import { AxiosError } from "axios"
import { getErrorMessageWithFallback } from "utils/api"

export type Selection =
  | { kind: "none" }
  | { kind: "rows"; ids: number[] }
  | { kind: "allMatching"; ids: number[]; excluded: number[] }

export type MatchingIdsAnswer =
  | { status: "loading" }
  | { status: "success"; ids: number[] }
  | { status: "tooMany"; detail: string }
  | { status: "error" }

export type SelectionBanner =
  | { kind: "loading"; loaded: number }
  | { kind: "offer"; loaded: number; total: number }
  | { kind: "tooMany"; loaded: number; detail: string }
  | { kind: "error"; loaded: number }
  | { kind: "allSelected"; total: number }
  | { kind: "partial"; selected: number; total: number }

export type HeaderState = "checked" | "indeterminate" | "unchecked"

export const emptySelection: Selection = { kind: "none" }

const rowsSelection = (ids: number[]): Selection =>
  ids.length ? { kind: "rows", ids } : emptySelection

export const isRowChecked = (selection: Selection, id: number) => {
  if (selection.kind === "rows") return selection.ids.includes(id)
  if (selection.kind === "allMatching") {
    return selection.ids.includes(id) && !selection.excluded.includes(id)
  }
  return false
}

// "All loaded selected": a rows selection covering every selectable loaded row.
export const isAllLoadedSelected = (
  selection: Selection,
  selectableIds: number[]
) =>
  selection.kind === "rows" &&
  selectableIds.length > 0 &&
  selectableIds.every((id) => selection.ids.includes(id))

export const toggleHeader = (
  selection: Selection,
  selectableIds: number[]
): Selection =>
  selection.kind === "allMatching" ||
  isAllLoadedSelected(selection, selectableIds)
    ? emptySelection
    : rowsSelection(selectableIds)

export const toggleRow = (selection: Selection, id: number): Selection => {
  if (selection.kind === "allMatching") {
    if (!selection.ids.includes(id)) return selection

    const excluded = selection.excluded.includes(id)
      ? selection.excluded.filter((x) => x !== id)
      : [...selection.excluded, id]
    return excluded.length === selection.ids.length
      ? emptySelection
      : { ...selection, excluded }
  }

  const ids = selection.kind === "rows" ? selection.ids : []
  return rowsSelection(
    ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
  )
}

export const selectAllMatching = (ids: number[]): Selection => ({
  kind: "allMatching",
  ids,
  excluded: [],
})

export const getDeletePayload = (selection: Selection): number[] => {
  if (selection.kind === "rows") return selection.ids
  if (selection.kind === "allMatching") {
    return selection.ids.filter((id) => !selection.excluded.includes(id))
  }
  return []
}

export const getSelectedCount = (selection: Selection) =>
  getDeletePayload(selection).length

export const getHeaderState = (
  selection: Selection,
  selectableIds: number[]
): HeaderState => {
  if (selection.kind === "none") return "unchecked"
  if (selection.kind === "allMatching") {
    return selection.excluded.length ? "indeterminate" : "checked"
  }
  return isAllLoadedSelected(selection, selectableIds)
    ? "checked"
    : "indeterminate"
}

export const getBanner = (
  selection: Selection,
  selectableIds: number[],
  answer: MatchingIdsAnswer
): SelectionBanner | null => {
  if (selection.kind === "allMatching") {
    const selected = getSelectedCount(selection)
    return selection.excluded.length
      ? { kind: "partial", selected, total: selection.ids.length }
      : { kind: "allSelected", total: selected }
  }

  if (!isAllLoadedSelected(selection, selectableIds)) return null

  const loaded = getSelectedCount(selection)
  switch (answer.status) {
    case "loading":
      return { kind: "loading", loaded }
    case "tooMany":
      return { kind: "tooMany", loaded, detail: answer.detail }
    case "error":
      return { kind: "error", loaded }
    case "success":
      return answer.ids.length > loaded
        ? { kind: "offer", loaded, total: answer.ids.length }
        : null
  }
}

// Maps the /ids query onto the model's answer; a 400 means more matches than
// the backend's limit, and its `detail` is a user-facing message (may be ""). A count being refreshed is still "loading", so an
// earlier count is never offered.
export const toMatchingIdsAnswer = (query: {
  data?: { data: number[] }
  error: AxiosError | null
  isError: boolean
  isFetching: boolean
}): MatchingIdsAnswer => {
  if (query.isFetching) return { status: "loading" }
  if (query.isError) {
    return query.error?.response?.status === 400
      ? {
          status: "tooMany",
          detail: getErrorMessageWithFallback(query.error, ""),
        }
      : { status: "error" }
  }
  return query.data
    ? { status: "success", ids: query.data.data }
    : { status: "loading" }
}
